<template>
  <div class="max-w-6xl mx-auto">
    <div class="bg-white rounded-lg shadow-md p-8">
      <PriorJobsTable
        :prior-results="priorResults"
        :loading="loadingPriorResults"
        @load-result="loadPriorResult"
        @delete-result="deletePriorResult"
        @refresh-list="fetchPriorResults"
      />
    </div>
  </div>
</template>

<script>
import PriorJobsTable from '../components/PriorJobsTable.vue'
import axios from 'axios'
import { buildApiUrl, API_ENDPOINTS } from '../config/api.js'
import { resolveJobIdentityFromResult } from '../utils/resultsS3Key.js'
import { useLabPath } from '../composables/useLabPath.js'

export default {
  name: 'CompletedJobs',
  inject: ['hideBackButton'],
  components: {
    PriorJobsTable
  },
  setup() {
    const toLab = useLabPath()
    return { toLab }
  },
  data() {
    return {
      priorResults: [],
      loadingPriorResults: false
    }
  },
  methods: {
    async fetchPriorResults() {
      this.loadingPriorResults = true
      this.priorResults = []

      try {
        const sessionId = localStorage.getItem('session_token') || 'test-session'
        const allResults = []
        const activeJobs = []

        const listPromise = axios.post(
          buildApiUrl(API_ENDPOINTS.RESULTS_LIST),
          {
            session_id: sessionId
          },
          {
            headers: { 'Content-Type': 'application/json' }
          }
        ).catch((error) => {
          console.warn('Failed to fetch prior results:', error)
          return null
        })

        const activePromise = axios.post(
          buildApiUrl(API_ENDPOINTS.PROCESS_ACTIVE),
          { session_id: sessionId },
          { headers: { 'Content-Type': 'application/json' } }
        ).catch((error) => {
          console.warn('Failed to fetch active processing jobs:', error)
          return null
        })

        const [listResponse, activeResponse] = await Promise.all([listPromise, activePromise])

        if (listResponse?.data?.success && listResponse.data.data) {
          for (const result of listResponse.data.data) {
            try {
              const detailResponse = await axios.post(
                buildApiUrl(API_ENDPOINTS.RESULTS_GET),
                {
                  session_id: sessionId,
                  s3_key: result.s3_key
                },
                {
                  headers: { 'Content-Type': 'application/json' }
                }
              )

              if (detailResponse.data.success && detailResponse.data.data) {
                const resultsData = detailResponse.data.data.results || detailResponse.data.data
                const summary = detailResponse.data.data.summary

                const { subid, dataset_id: datasetId } = resolveJobIdentityFromResult(result)

                const firstDate = this.extractFirstDate(resultsData)
                const lastDate = this.extractLastDate(resultsData)

                allResults.push({
                  s3_key: result.s3_key,
                  subid: subid,
                  dataset_id: datasetId,
                  processed_date: result.started_at,
                  first_date: firstDate,
                  last_date: lastDate,
                  results_data: resultsData,
                  summary: summary,
                  status: 'completed'
                })
              }
            } catch (error) {
              console.warn(`Failed to fetch details for ${result.s3_key}:`, error)
              const { subid, dataset_id } = resolveJobIdentityFromResult(result)

              allResults.push({
                s3_key: result.s3_key,
                subid: subid,
                dataset_id: dataset_id,
                processed_date: result.started_at,
                first_date: null,
                last_date: null,
                status: 'completed'
              })
            }
          }
        }

        if (activeResponse?.data?.success && Array.isArray(activeResponse.data.data)) {
          for (const job of activeResponse.data.data) {
            activeJobs.push({
              s3_key: null,
              subid: job.subid,
              dataset_id: job.dataset_id,
              processed_date: job.submitted_at,
              curve_count: null,
              day_count: null,
              first_date: null,
              last_date: null,
              status: 'processing',
              batch_job_id: job.batch_job_id,
              batch_status: job.batch_status
            })
          }
        }

        const resultsMap = new Map()

        for (const result of allResults) {
          const key = `${result.subid}_${result.dataset_id}`
          const existing = resultsMap.get(key)

          if (!existing) {
            resultsMap.set(key, result)
          } else {
            const existingDate = new Date(existing.processed_date)
            const currentDate = new Date(result.processed_date)

            if (currentDate > existingDate) {
              resultsMap.set(key, result)
            }
          }
        }

        for (const job of activeJobs) {
          const key = (job.subid != null && job.dataset_id != null)
            ? `${job.subid}_${job.dataset_id}`
            : `batch-${job.batch_job_id || Math.random()}`
          resultsMap.set(key, job)
        }

        const uniqueResults = Array.from(resultsMap.values())
        uniqueResults.sort((a, b) => {
          if (a.status === 'processing' && b.status !== 'processing') return -1
          if (b.status === 'processing' && a.status !== 'processing') return 1
          const dateA = new Date(a.processed_date || 0)
          const dateB = new Date(b.processed_date || 0)
          return dateB - dateA
        })

        this.priorResults = uniqueResults
      } catch (error) {
        console.error('Error fetching prior results:', error)
      } finally {
        this.loadingPriorResults = false
      }
    },
    
    extractFirstDate(resultsData) {
      if (!resultsData || !resultsData.processed_dataset) return null
      
      const dataset = resultsData.processed_dataset
      if (Array.isArray(dataset) && dataset.length > 0) {
        const firstRow = dataset[0]
        if (firstRow && firstRow.datetime) {
          return firstRow.datetime
        }
      }
      return null
    },
    
    extractLastDate(resultsData) {
      if (!resultsData || !resultsData.processed_dataset) return null
      
      const dataset = resultsData.processed_dataset
      if (Array.isArray(dataset) && dataset.length > 0) {
        const lastRow = dataset[dataset.length - 1]
        if (lastRow && lastRow.datetime) {
          return lastRow.datetime
        }
      }
      return null
    },
    
    async loadPriorResult(result) {
      try {
        console.log('Loading prior result:', result)
        
        // Fetch full results data
        const sessionId = localStorage.getItem('session_token') || 'test-session'
        const response = await axios.post(
          buildApiUrl(API_ENDPOINTS.RESULTS_GET),
          {
            session_id: sessionId,
            s3_key: result.s3_key
          },
          {
            headers: { 'Content-Type': 'application/json' }
            // Note: withCredentials removed - session_id is in request body
          }
        )

        if (!response.data.success || !response.data.data) {
          throw new Error('Failed to fetch result data')
        }

        const resultsData = response.data.data.results || response.data.data
        const summary = response.data.data.summary || {}
        
        // Extract metadata
        const dataMetadata = {
          total_rows: summary.data_rows || (resultsData.processed_dataset?.length || 0),
          columns: resultsData.processed_dataset?.[0] ? Object.keys(resultsData.processed_dataset[0]) : []
        }
        
        // Extract only metadata (small data) - exclude processed_dataset which can be 41MB+
        // NOTE: Don't store the old session_id - it won't exist in backend storage anymore
        // For prior jobs, session_id should be null to indicate this is a read-only view
        const metadataOnly = {
          session_id: null,  // Prior jobs don't have active sessions
          subid: result.subid,
          dataset_identifier: result.dataset_id,
          s3_key: result.s3_key,
          curve_features: resultsData.curve_features || [],
          day_level_data: resultsData.day_level_data || [],
          processing_summary: resultsData.processing_summary || {},
          data_metadata: dataMetadata,
          data_timezone: resultsData.data_timezone || 'US/Central',
          column_metadata: resultsData.column_metadata || {},
          drinking_day_score_meta: resultsData.drinking_day_score_meta || {},
          // Applied Daily Tracking thresholds — must survive prior-job reload
          drinking_prediction_review: resultsData.drinking_prediction_review || null,
          adherence_review: resultsData.adherence_review || null,
          processed_dataset: undefined,
          is_prior_job: true  // Flag to indicate this is a prior job (read-only)
        }
        
        // Store metadata in sessionStorage
        sessionStorage.setItem('processingResults', JSON.stringify(metadataOnly))
        
        // Also store configured data metadata for SubjectDashboard
        // NOTE: session_id is null for prior jobs - reprocessing requires re-uploading
        // Store the s3_key as prior_job_s3_key so it can be deleted when reprocessing
        const configuredData = {
          session_id: null,  // Prior jobs don't have active sessions
          subid: result.subid,
          dataset_identifier: result.dataset_id,
          data_stored_in_s3: true,
          s3_key: result.s3_key,
          prior_job_s3_key: result.s3_key,  // Store for deletion when reprocessing
          is_prior_job: true  // Flag to indicate this is a prior job
        }
        sessionStorage.setItem('configuredData', JSON.stringify(configuredData))
        
        // Navigate to subject dashboard
        this.$router.push({
          path: this.toLab('/subject-dashboard'),
          query: {
            subid: result.subid,
            dataset: result.dataset_id,
            fromPrior: 'true',
            s3_key: result.s3_key
          }
        })
      } catch (error) {
        console.error('Error loading prior result:', error)
        alert('Failed to load prior result. Please try again.')
      }
    },
    
    async deletePriorResult(result) {
      try {
        const sessionId = localStorage.getItem('session_token') || 'test-session'
        
        // Call API endpoint to delete from S3
        const response = await axios.post(
          buildApiUrl(API_ENDPOINTS.RESULTS_DELETE),
          {
            session_id: sessionId,
            s3_key: result.s3_key
          },
          {
            headers: { 'Content-Type': 'application/json' }
            // Note: withCredentials removed - session_id is in request body
          }
        )
        
        if (response.data.success) {
          // Remove from local state
          this.priorResults = this.priorResults.filter(r => r.s3_key !== result.s3_key)
          console.log('✅ Deleted prior result:', result.s3_key)
        } else {
          throw new Error(response.data.message || 'Delete failed')
        }
      } catch (error) {
        console.error('Error deleting prior result:', error)
        alert(`Failed to delete prior result: ${error.response?.data?.message || error.message}. Please try again.`)
      }
    }
  },
  mounted() {
    this.hideBackButton()
    this.fetchPriorResults()
  }
}
</script>

